import { Nav } from '@/components/Nav';
import { Footer } from '@/components/Footer';
import { BookingWidget } from '@/components/BookingWidget';
import { Services } from '@/components/Services';
import { ContactForm } from '@/components/ContactForm';
import { Clock, ShieldCheck, Star, Award } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1676754568744-7852efc67c40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';
const HERO_VIDEO = 'https://videos.pexels.com/video-files/16762623/16762623-hd_720_1280_30fps.mp4';

export default function Landing() {
    return (
        <div className="bg-[#050505] min-h-screen text-[#F8F8F5]">
            <Nav />

            {/* HERO */}
            <section data-testid="hero-section" className="relative pt-32 pb-20 min-h-[100vh] overflow-hidden">
                <div className="absolute inset-0">
                    <video
                        data-testid="hero-video"
                        src={HERO_VIDEO}
                        poster={HERO_IMG}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        className="w-full h-full object-cover"
                        aria-label="Tesla Model Y noire roulant la nuit"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[80vh]">
                        <div className="lg:col-span-6 space-y-8">
                            <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] animate-fade-up">
                                — Arnoux &amp; co Taxi
                            </div>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight animate-fade-up-delay-1">
                                L'art du déplacement,<br />
                                <span className="italic text-[#C5A059]">en toute discrétion.</span>
                            </h1>
                            <p className="text-[#A3A39D] text-base md:text-lg leading-relaxed max-w-xl animate-fade-up-delay-2">
                                Chauffeur privé d'exception, disponible 24h/24 et 7j/7. Transferts aéroports, événements VIP, transport médical — chaque trajet, une expérience d'exception.
                            </p>
                            <div className="flex flex-wrap gap-8 pt-6 animate-fade-up-delay-3">
                                <div className="flex items-center gap-3"><Clock size={18} className="text-[#C5A059]" /><span className="text-sm text-[#A3A39D]">24/7</span></div>
                                <div className="flex items-center gap-3"><ShieldCheck size={18} className="text-[#C5A059]" /><span className="text-sm text-[#A3A39D]">Chauffeurs agréés</span></div>
                                <div className="flex items-center gap-3"><Star size={18} className="text-[#C5A059]" /><span className="text-sm text-[#A3A39D]">Service VIP</span></div>
                                <div className="flex items-center gap-3"><Award size={18} className="text-[#C5A059]" /><span className="text-sm text-[#A3A39D]">Premium</span></div>
                            </div>
                        </div>

                        <div className="lg:col-span-6 animate-fade-up-delay-2">
                            <BookingWidget />
                        </div>
                    </div>
                </div>
            </section>

            {/* QUOTE / About */}
            <section data-testid="about-section" className="py-24 md:py-32 bg-black border-y border-white/5">
                <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">Notre engagement</div>
                    <p data-testid="about-slogan" className="font-display text-3xl md:text-5xl font-light leading-tight tracking-tight">
                        « Notre force, c'est <em className="text-[#C5A059] not-italic">notre engagement</em> à satisfaire chacun de vos déplacements. »
                    </p>
                    <div className="mt-12 gold-divider w-32 mx-auto" />
                </div>
            </section>

            {/* SERVICES */}
            <Services />

            {/* WHY US */}
            <section data-testid="why-section" className="py-24 md:py-32 bg-black border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                        <div className="md:col-span-5">
                            <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Pourquoi nous</div>
                            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight">L'excellence,<br /><span className="italic text-[#C5A059]">sans compromis.</span></h2>
                        </div>
                        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#222]">
                            {[
                                ['01', 'Ponctualité', 'Suivi en temps réel, anticipation des trafics, garantie d\'arrivée.'],
                                ['02', 'Discrétion', 'Chauffeur formé au protocole VIP, confidentialité absolue.'],
                                ['03', 'Deux véhicules', 'Tesla Model Y (4 pax) ou Van 8 places pour groupes et bagages volumineux.'],
                                ['04', 'Disponibilité', 'Service 24/7, intervention rapide sur toute la région.'],
                            ].map(([n, t, d]) => (
                                <div key={n} className="bg-black p-8">
                                    <div className="font-display text-3xl text-[#C5A059] font-light">{n}</div>
                                    <div className="text-base font-medium mt-3 text-[#F8F8F5]">{t}</div>
                                    <p className="text-sm text-[#A3A39D] mt-2 leading-relaxed">{d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#050505]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Contactez-nous</div>
                        <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-8">
                            Une question,<br /><span className="italic text-[#C5A059]">une demande&nbsp;?</span>
                        </h2>
                        <p className="text-[#A3A39D] text-base leading-relaxed mb-12">
                            Notre équipe est disponible 24h/24 pour répondre à toutes vos demandes et organiser vos déplacements sur-mesure.
                        </p>
                        <div className="space-y-4 text-sm">
                            <div><div className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Téléphone</div><a href="tel:+33603349423" className="text-[#F8F8F5] mt-1 hover:text-[#C5A059] block">06 03 34 94 23</a></div>
                            <div><div className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Email</div><a href="mailto:danyarnoux81@hotmail.com" className="text-[#F8F8F5] mt-1 hover:text-[#C5A059] block">danyarnoux81@hotmail.com</a></div>
                            <div><div className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Disponibilité</div><div className="text-[#F8F8F5] mt-1">24h/24 — 7j/7</div></div>
                        </div>
                    </div>
                    <div>
                        <ContactForm />
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
